import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { add, curry, pipe, slice, trim, split, map, concat, drop, take, toLower, reduce, startsWith, head, replace, assoc, keys, prop, has, find, propEq, join, defaultTo } from 'ramda';
import { bind } from 'redux-effects';
import { fetch as fetch$1 } from 'redux-effects-fetch';
import { startTrackingRequest, stopTrackingRequest } from '@rushplay/api-client';

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      writable: true,
      configurable: true
    }
  });
  if (superClass) _setPrototypeOf(subClass, superClass);
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

function _possibleConstructorReturn(self, call) {
  if (call && (typeof call === "object" || typeof call === "function")) {
    return call;
  }

  return _assertThisInitialized(self);
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

var CaptureContext = React.createContext();
function TranslationCapture(props) {
  return React.createElement(CaptureContext.Provider, {
    value: {
      onKeyUse: props.onKeyUse
    }
  }, React.Children.only(props.children));
}
TranslationCapture.propTypes = {
  children: PropTypes.node,
  onKeyUse: PropTypes.func.isRequired
};

var E_TOKEN_LENGTH = new SyntaxError('Left and right tokens must have equal width');
var LEFT_TOKEN = '{{';
var RIGHT_TOKEN = '}}';
var PIPE_TOKEN = '|';
var PARAMETERS_LEFT_TOKEN = '(';
var PARAMETERS_RIGHT_TOKEN = ')';
var E_TOKEN_MISSING = new SyntaxError("One of closing tokens '".concat(RIGHT_TOKEN, "' not found"));
var TOKEN_LENGTH = LEFT_TOKEN.length === RIGHT_TOKEN.length && LEFT_TOKEN.length;
var PARAMETERS_TOKEN_LENGTH = PARAMETERS_LEFT_TOKEN.length === PARAMETERS_RIGHT_TOKEN.length && PARAMETERS_LEFT_TOKEN.length; // sanity check

if (!TOKEN_LENGTH || !PARAMETERS_TOKEN_LENGTH) {
  throw E_TOKEN_LENGTH;
} // shortcut for token offset calculation


var offset = add(TOKEN_LENGTH);
var parametersOffset = add(PARAMETERS_TOKEN_LENGTH); // string-friendly slice with trim

var extractItem = curry(function (start, end, input) {
  return pipe( // get desired part
  slice(start, end), // trim unneeded space
  trim)(input);
}); // raw parameters extraction helper

var extractParameters = curry(function (start, end, input) {
  return pipe( // get desired part
  extractItem(start, end), // split by commas
  split(','), // trim unneded space for every item
  map(trim))(input);
}); // --

function parseProcessor(input) {
  var leftStart = input.indexOf(PARAMETERS_LEFT_TOKEN);
  var rightStart = input.indexOf(PARAMETERS_RIGHT_TOKEN);

  if (leftStart === -1 || rightStart === -1) {
    // if one of tokens is present
    if (leftStart !== rightStart) {
      if (__DEV__) {
        // eslint-disable-next-line no-console
        console.warn("Brackets mismatch in processor declaration: ".concat(input));
      }

      return null;
    }

    return {
      name: input,
      type: 'processor',
      parameters: []
    };
  }

  var leftEnd = parametersOffset(leftStart);
  var name = take(leftStart, input);
  var parameters = extractParameters(leftEnd, rightStart, input);
  return {
    name: name,
    type: 'processor',
    parameters: parameters
  };
}

function parseVariable(input) {
  // if PIPE_TOKEN is present
  if (input.indexOf(PIPE_TOKEN) > -1) {
    // extract variable name and processor
    var _pipe = pipe(split(PIPE_TOKEN), map(trim))(input),
        _pipe2 = _slicedToArray(_pipe, 2),
        name = _pipe2[0],
        processorString = _pipe2[1];

    var processor = parseProcessor(processorString);
    return {
      name: name,
      type: 'variable',
      processor: processor
    };
  } // else treat input as variable name


  return {
    name: trim(input),
    type: 'variable'
  };
} // --


function parse(input, _result) {
  var leftStart = input.indexOf(LEFT_TOKEN);
  var rightStart = input.indexOf(RIGHT_TOKEN);
  var leftEnd = offset(leftStart);
  var rightEnd = offset(rightStart); // create shortcut function

  function appendResult(x) {
    return concat(_result || [], [x]);
  }

  if (leftStart === -1 || rightStart === -1) {
    // if one of tokens is present
    if (leftStart !== rightStart) {
      throw E_TOKEN_MISSING;
    }
  } // if LEFT_TOKEN is present, start recursion


  if (leftStart > -1) {
    // if string begins with LEFT_TOKEN
    if (leftStart === 0) {
      // extract variable
      var _item = parseVariable(extractItem(leftEnd, rightStart, input));

      return parse(drop(rightEnd, input), appendResult(_item));
    } // else extract string up to the next LEFT_TOKEN


    var item = take(leftStart, input);
    return parse(drop(leftStart, input), appendResult(item));
  } // if we have string left


  if (input.length) {
    // append it to the result
    return appendResult(input);
  } // else just return the result


  return _result;
}

var ALIAS_TOKEN = '@:';
var INITIAL_STATE = {};
var TRANSLATIONS_UPDATED = '@rushplay/i18n/TRANSLATIONS_UPDATED';

function prepareTranslations(language, translations) {
  return reduce(function (result, key) {
    var parsedValue = parse(translations[key]);

    if (startsWith(ALIAS_TOKEN, head(parsedValue))) {
      parsedValue[0] = replace(ALIAS_TOKEN, "".concat(ALIAS_TOKEN).concat(language, ":"), parsedValue[0]);
    }

    return assoc("".concat(language, ":").concat(key), parsedValue, result);
  }, {}, keys(translations));
}

function updateTranslations(translations) {
  return {
    type: TRANSLATIONS_UPDATED,
    error: translations instanceof Error,
    payload: translations
  };
}
var CACHE = {};
function fetch(cdnUrl, language) {
  var url = "".concat(cdnUrl, "/locales/speedy_").concat(toLower(language), ".json");
  var now = Date.now();
  var cached = CACHE[url] || {
    at: 0
  };

  if (now - cached.at < 30000) {
    return cached.value;
  }
  /*
   * TODO: We must track it as API request to make server wait for translations
   * to be downloaded.
   *
   * 1. Tracking must be extracted from API Client as it is independent piece
   * of logic.
   *
   * 2. Translations must be reworked not to fetch all the content. Possibly
   * look into `/translations` endpoint and `babel-plugin-tranform-translate`
   * branch in `@rushplay/i18n`
   */


  return [startTrackingRequest('translations'), bind(fetch$1(url), function (res) {
    var translations = prepareTranslations(language, res.value);
    var action = updateTranslations(translations);
    CACHE[url] = {
      at: now,
      value: action
    };
    return [action, stopTrackingRequest('translations')];
  }, function () {
    return stopTrackingRequest('translations');
  })];
}
function reducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : INITIAL_STATE;
  var action = arguments.length > 1 ? arguments[1] : undefined;

  if (action.error) {
    return state;
  }

  switch (action.type) {
    case TRANSLATIONS_UPDATED:
      {
        return action.payload;
      }

    default:
      {
        return state;
      }
  }
}
function getKeys(state) {
  return keys(state);
}
/**
 * Get translation string for given key
 * @param {Object} state
 * @param {Object} props
 * @param {string} props.translationKey
 * @returns {?string}
 */

function getTranslation(state, props) {
  var translation = prop(props.translationKey, state);

  if (translation && startsWith(ALIAS_TOKEN, head(translation))) {
    var translationKey = replace(ALIAS_TOKEN, '', head(translation));
    return getTranslation(state, {
      translationKey: translationKey
    });
  }

  return translation;
}
/**
 * Check if translation exists
 * @param {Object} state
 * @param {Object} props
 * @param {string} props.translationKey
 * @returns {boolean}
 */

function hasTranslation(state, props) {
  return has(props.translationKey, state);
}

// dashToMinus :: String -> String
function dashToMinus(input) {
  // You may not notice the difference in monospaced font
  return input.replace('-', '−');
} // splitParts :: String -> String


function splitParts(string) {
  var result = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';

  if (string === '') {
    return dashToMinus(result.trim());
  }

  return splitParts(string.slice(0, -3), "".concat(string.slice(-3), " ").concat(result));
} // toIntFloat :: Number -> [String]


function toIntFloat(number, _fixed) {
  var fixed = _fixed && typeof _fixed === 'boolean' ? 2 : _fixed;
  var string = parseInt(fixed, 10) ? Math.floor(number).toFixed(fixed) : String(number);
  return string.split('.');
} // formatPrice :: Number -> String


function formatPrice(input, fixed) {
  if (typeof input !== 'number') {
    throw new Error('Must be a Number');
  }

  var arr = toIntFloat(input, fixed);
  return arr[1] ? "".concat(splitParts(arr[0]), ".").concat(arr[1]) : splitParts(arr[0]);
}

/**
 * This is the list of currencies used in application with some hints
 * for proper rendering.
 *
 * `code` is a currency code according to ISO 4217
 */
var CURRENCIES = [{
  code: 'CAD',
  customCurrency: false,
  ratio: 100
}, {
  code: 'GBP',
  customCurrency: false,
  ratio: 100
}, {
  code: 'EUR',
  customCurrency: false,
  ratio: 100
}, {
  code: 'INR',
  customCurrency: false,
  ratio: 100
}, {
  code: 'NOK',
  customCurrency: false,
  ratio: 100
}, {
  code: 'RUBIES',
  customCurrency: true,
  ratio: 1
}, {
  code: 'SEK',
  customCurrency: false,
  ratio: 100
}, {
  code: 'USD',
  customCurrency: false,
  ratio: 100
}];

function fromCents() {
  var ratio = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 1;
  var amount = arguments.length > 1 ? arguments[1] : undefined;
  return amount / ratio;
}

function minimumFractionDigits(decimals, amount) {
  switch (decimals) {
    case 'hide':
      {
        return 0;
      }

    default:
      {
        // Remove decimals if it’s a integer-ish number (i.e. 7.00)
        return Math.round(amount) === amount ? 0 : 2;
      }
  }
} // toLocaleCurrency :: Currency -> Number -> String


function toLocaleCurrency(data, amount, fixed) {
  var _find = find(propEq('code', data.currencyCode))(CURRENCIES),
      customCurrency = _find.customCurrency,
      ratio = _find.ratio;

  var decimals = fixed ? 'show' : 'auto';
  return pipe( // divide if necessary
  function (amount) {
    return fromCents(ratio, amount);
  }, function (amount) {
    return new Intl.NumberFormat(data.locale, {
      // We pass `undefined` here because Intl.NumberFormat doesn't accept `null` as currency
      currency: customCurrency ? undefined : data.currencyCode,
      minimumFractionDigits: minimumFractionDigits(decimals, amount),
      maximumFractionDigits: 2,
      style: customCurrency ? 'decimal' : 'currency'
    }).format(amount);
  })(amount);
}

function currency(input, data, fixed) {
  return toLocaleCurrency(data, parseFloat(input), fixed);
}
function currencyFixed(input, data) {
  return currency(input, data, true);
}
function groupDigits(input) {
  return formatPrice(input);
}
function titlecase(input) {
  return input.split(' ').map(function (word) {
    return word.charAt(0).toUpperCase() + word.slice(1);
  }).join(' ');
}
function uppercase(input) {
  return input.toUpperCase();
}

var processors = /*#__PURE__*/Object.freeze({
  currency: currency,
  currencyFixed: currencyFixed,
  groupDigits: groupDigits,
  titlecase: titlecase,
  uppercase: uppercase
});

var E_MISSING_PROCESSOR = new Error('Specified processor not found');
var E_UNKNOWN_ENTITY = new SyntaxError('Unknown entity');

function processVariable(processor, data, value, loose) {
  if (loose) {
    return null;
  }

  if (processor == null) {
    throw E_MISSING_PROCESSOR;
  }

  return processor(value, data);
}

function stringifyVariable(_ref, data, value, loose) {
  var name = _ref.name,
      processor = _ref.processor;

  if (!loose && value == null) {
    return "{{".concat(name, "}}");
  }

  if (processor) {
    // eslint-disable-next-line import/namespace
    return processVariable(processors[processor.name], data, value, loose);
  }

  return value;
}

function stringify(input, data, variables, loose) {
  if (input.length === 1 && typeof input[0] === 'string') {
    return input[0];
  } // transform everything to strings


  return join('', map(function (x) {
    // return string as is
    if (typeof x === 'string') {
      return x;
    } // if `type` is `variable`, stringify it using given data


    if (x && x.type && x.type === 'variable') {
      var valueFromVariables = (variables || {})[x.name];
      var valueFromData = (data || {})[x.name];
      return stringifyVariable(x, data || {}, defaultTo(valueFromData, valueFromVariables), loose);
    } // otherwise throw


    throw E_UNKNOWN_ENTITY;
  }, input));
}

var TranslationContext = React.createContext();

var TranslationProviderBase =
/*#__PURE__*/
function (_React$PureComponent) {
  _inherits(TranslationProviderBase, _React$PureComponent);

  function TranslationProviderBase() {
    var _this;

    _classCallCheck(this, TranslationProviderBase);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(TranslationProviderBase).call(this));
    _this.hasTranslation = _this.hasTranslation.bind(_assertThisInitialized(_this));
    _this.translate = _this.translate.bind(_assertThisInitialized(_this));
    return _this;
  }

  _createClass(TranslationProviderBase, [{
    key: "hasTranslation",
    value: function hasTranslation(key) {
      var resolvedKey = this.props.keyResolver(this.props.keys, key);

      if (this.context && this.context.onKeyUse) {
        this.context.onKeyUse(resolvedKey);
      }

      return this.props.hasTranslation(resolvedKey);
    }
  }, {
    key: "translate",
    value: function translate(key, variables) {
      var resolvedKey = this.props.keyResolver(this.props.keys, key);

      if (this.context && this.context.onKeyUse) {
        this.context.onKeyUse(resolvedKey);
      }

      var translation = this.props.getTranslation(resolvedKey);

      if (!translation) {
        return "[".concat(resolvedKey, "]");
      }

      return stringify(translation, this.props.globals, variables);
    }
  }, {
    key: "render",
    value: function render() {
      return React.createElement(TranslationContext.Provider, {
        value: {
          isExistingTranslation: this.hasTranslation,
          translate: this.translate
        }
      }, React.Children.only(this.props.children));
    }
  }]);

  return TranslationProviderBase;
}(React.PureComponent);

TranslationProviderBase.contextType = CaptureContext;
TranslationProviderBase.propTypes = {
  children: PropTypes.element,
  globals: PropTypes.object,
  getTranslation: PropTypes.func,
  keyResolver: PropTypes.func,
  keys: PropTypes.arrayOf(PropTypes.string),
  hasTranslation: PropTypes.func
};

function makeMapStateToProps() {
  var prevTranslations = null;
  var prevGetTranslation = null;
  var prevKeys = null;
  var prevHasTranslation = null;
  return function mapStateToProps(state, props) {
    var translations = props.stateResolver(state);
    var translationsChanged = prevTranslations !== translations;
    prevTranslations = translations;
    prevGetTranslation = translationsChanged ? function (translationKey) {
      return getTranslation(translations, {
        translationKey: translationKey
      });
    } : prevGetTranslation;
    prevKeys = translationsChanged ? getKeys(translations) : prevKeys;
    prevHasTranslation = translationsChanged ? function (translationKey) {
      return hasTranslation(translations, {
        translationKey: translationKey
      });
    } : prevHasTranslation;
    return {
      globals: props.variableResolver(state),
      getTranslation: prevGetTranslation,
      keys: prevKeys,
      hasTranslation: prevHasTranslation
    };
  };
}

var connector = connect(makeMapStateToProps);
var TranslationProvider = connector(TranslationProviderBase);

function useI18n() {
  return useContext(TranslationContext);
}

function withTranslate(WrappedComponent) {
  return function TranslationWrapper(props) {
    var context = useI18n();
    return React.createElement(WrappedComponent, Object.assign({}, props, context));
  };
}

export { CaptureContext, TRANSLATIONS_UPDATED, TranslationCapture, TranslationContext, TranslationProvider, fetch, getKeys, getTranslation, hasTranslation, reducer, updateTranslations, useI18n, withTranslate };
